import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, ArrowRight, Package, Plus, Minus, Trash2, Warehouse, Store, MapPin, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { InventoryItem, LocationInfo, WorkerInfo } from '../../types';
import { WorkerSelector } from './WorkerSelector';
import { LocationSelector } from './LocationSelector';
import { supabase } from '../../lib/supabase';

interface ColorSize {
  id: string;
  name: string;
}

interface ReturnItem {
  product_id: string;
  product: InventoryItem;
  quantity: number;
  available_quantity: number;
}

interface ReturnStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateReturn: (
    items: Array<{ product_id: string; quantity: number; source_location_id: string; destination_location_id: string }>,
    workerId: string,
    workerName: string,
    notes?: string
  ) => Promise<any>;
  processing: boolean;
  products: InventoryItem[];
  colors: ColorSize[];
  sizes: ColorSize[];
  locations: LocationInfo[];
  adminId: string;
}

export const ReturnStockModal: React.FC<ReturnStockModalProps> = ({
  isOpen,
  onClose,
  onCreateReturn,
  processing,
  products,
  colors,
  sizes,
  locations,
  adminId
}) => {
  const [sourceLocationId, setSourceLocationId] = useState<string | null>(null);
  const [destinationLocationId, setDestinationLocationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [worker, setWorker] = useState<WorkerInfo | null>(null);
  const [notes, setNotes] = useState('');
  const [locationInventory, setLocationInventory] = useState<Record<string, number>>({});

  useEffect(() => {
    if (isOpen && locations.length > 0) {
      const shopLocation = locations.find(l => l.name.toLowerCase().includes('shop'));
      const warehouseLocation = locations.find(l =>
        l.name.toLowerCase().includes('warehouse') || l.name.toLowerCase().includes('wearhouse')
      );

      if (shopLocation && !sourceLocationId) {
        setSourceLocationId(shopLocation.id);
      }
      if (warehouseLocation && !destinationLocationId) {
        setDestinationLocationId(warehouseLocation.id);
      }
    }
  }, [isOpen, locations]);

  useEffect(() => {
    if (sourceLocationId) {
      fetchLocationInventory(sourceLocationId);
    }
  }, [sourceLocationId]);

  const fetchLocationInventory = async (locationId: string) => {
    try {
      const { data, error } = await supabase
        .from('product_location_inventory')
        .select('product_id, quantity')
        .eq('location_id', locationId)
        .gt('quantity', 0);

      if (error) throw error;

      const inventory: Record<string, number> = {};
      (data || []).forEach(item => {
        inventory[item.product_id] = item.quantity;
      });
      setLocationInventory(inventory);
    } catch (err) {
      console.error('Error fetching location inventory:', err);
    }
  };

  const handleClose = () => {
    setReturnItems([]);
    setSearchQuery('');
    setNotes('');
    onClose();
  };

  const availableProducts = products.filter(p => {
    const qty = locationInventory[p.id] || 0;
    if (qty <= 0) return false;
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(query) ||
      p.reference_code?.toLowerCase().includes(query) ||
      p.barcode?.toLowerCase().includes(query)
    );
  });

  const addToReturn = (product: InventoryItem) => {
    const availableQty = locationInventory[product.id] || 0;
    if (availableQty <= 0) return;

    const existing = returnItems.find(item => item.product_id === product.id);
    if (existing) {
      if (existing.quantity < availableQty) {
        setReturnItems(prev => prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));
      }
    } else {
      setReturnItems(prev => [...prev, {
        product_id: product.id,
        product,
        quantity: 1,
        available_quantity: availableQty
      }]);
    }
  };

  const updateReturnQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromReturn(productId);
      return;
    }

    setReturnItems(prev => prev.map(item =>
      item.product_id === productId
        ? { ...item, quantity: Math.min(quantity, item.available_quantity) }
        : item
    ));
  };

  const removeFromReturn = (productId: string) => {
    setReturnItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const handleCreateReturn = async () => {
    if (!sourceLocationId || !destinationLocationId || !worker || returnItems.length === 0) return;

    const items = returnItems.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      source_location_id: sourceLocationId,
      destination_location_id: destinationLocationId
    }));

    const result = await onCreateReturn(items, worker.id, worker.full_name, notes || undefined);
    if (result) {
      handleClose();
    }
  };

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '-';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '-';

  const getLocationIcon = (name?: string) => {
    if (!name) return MapPin;
    const lowerName = name.toLowerCase();
    if (lowerName.includes('warehouse') || lowerName.includes('wearhouse')) return Warehouse;
    if (lowerName.includes('shop') || lowerName.includes('store')) return Store;
    return MapPin;
  };

  const sourceLocation = locations.find(l => l.id === sourceLocationId);
  const destLocation = locations.find(l => l.id === destinationLocationId);
  const totalItems = returnItems.reduce((sum, item) => sum + item.quantity, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Return Stock
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Move items back to warehouse
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Return From (Source)
              </label>
              <LocationSelector
                locations={locations}
                selectedId={sourceLocationId}
                onSelect={setSourceLocationId}
                mode="source"
                compact
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Return To (Destination)
              </label>
              <LocationSelector
                locations={locations.filter(l => l.id !== sourceLocationId)}
                selectedId={destinationLocationId}
                onSelect={setDestinationLocationId}
                mode="destination"
                compact
              />
            </div>
          </div>

          {sourceLocationId && destinationLocationId && (
            <div className="flex items-center justify-center gap-3 mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2">
                {React.createElement(getLocationIcon(sourceLocation?.name), {
                  className: 'h-4 w-4 text-amber-600 dark:text-amber-400'
                })}
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {sourceLocation?.name}
                </span>
              </div>
              <ArrowRight className="h-5 w-5 text-amber-500" />
              <div className="flex items-center gap-2">
                {React.createElement(getLocationIcon(destLocation?.name), {
                  className: 'h-4 w-4 text-amber-600 dark:text-amber-400'
                })}
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {destLocation?.name}
                </span>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Worker *
            </label>
            <WorkerSelector
              selectedWorkerId={worker?.id || null}
              onSelect={setWorker}
              placeholder="Select who is returning stock..."
              autoSelectCurrent
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Available at {sourceLocation?.name || 'Source'}
                </h3>
              </div>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {!sourceLocationId ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <MapPin className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a source location</p>
                  </div>
                ) : availableProducts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No products available</p>
                  </div>
                ) : (
                  availableProducts.slice(0, 50).map(product => {
                    const inReturn = returnItems.find(item => item.product_id === product.id);
                    const availableQty = locationInventory[product.id] || 0;

                    return (
                      <button
                        key={product.id}
                        onClick={() => addToReturn(product)}
                        disabled={inReturn && inReturn.quantity >= availableQty}
                        className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors text-left disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                              {product.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                              {product.reference_code && <span>{product.reference_code}</span>}
                              {product.color_id && (
                                <span className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                                  {getColorName(product.color_id)}
                                </span>
                              )}
                              {product.size_id && (
                                <span className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                                  {getSizeName(product.size_id)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {availableQty} avail
                            </span>
                            <Plus className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Items to Return ({returnItems.length})
                </h3>
                {returnItems.length > 0 && (
                  <button
                    onClick={() => setReturnItems([])}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {returnItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Add items to return</p>
                  </div>
                ) : (
                  returnItems.map(item => (
                    <div
                      key={item.product_id}
                      className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                            {item.product.name}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {item.product.color_id && (
                              <span className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded">
                                {getColorName(item.product.color_id)}
                              </span>
                            )}
                            {item.product.size_id && (
                              <span className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded">
                                {getSizeName(item.product.size_id)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Max: {item.available_quantity}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700 p-0.5">
                            <button
                              onClick={() => updateReturnQuantity(item.product_id, item.quantity - 1)}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-8 text-center text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateReturnQuantity(item.product_id, item.quantity + 1)}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                              disabled={item.quantity >= item.available_quantity}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>

                          <button
                            onClick={() => removeFromReturn(item.product_id)}
                            className="p-1.5 text-red-500 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Reason / Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why are these items being returned?"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={2}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Total items to return
            </span>
            <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
              {totalItems}
            </span>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="flex-1"
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateReturn}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              loading={processing}
              disabled={returnItems.length === 0 || !worker || !sourceLocationId || !destinationLocationId}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Create Return Transfer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
